import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Send as PostIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import { IncomeStatusChip, SchedulePicker, fmtDate, getErrorMessage, unwrap } from './IncomeShared';

const STATUSES = ['DRAFT', 'POSTED', 'VOID'];

// Read-only: individual per-period postings a revenue schedule generates
// once approved/posted (see RevenueSchedulesTab.js). Only "Post" is
// exposed here - creation and voiding belong to the parent schedule.
const RevenueRecognitionsTab = () => {
  const { can } = useAccessControl();
  const canApprove = can(RESOURCES.LEDGER.INCOME, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [detail, setDetail] = useState(null);

  const reload = () => setRefreshKey((k) => k + 1);

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getRevenueRecognition(row.recognition_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch recognition'), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.INCOME}
        title="Revenue Recognitions"
        subtitle="Per-period postings generated by a revenue schedule"
        idKey="recognition_id"
        searchPlaceholder="Search schedule #..."
        emptyMessage="No revenue recognitions found. These appear once a schedule is posted."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getRevenueRecognitions(params)}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'schedule_id', label: 'Schedule', picker: SchedulePicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Period' },
        ]}
        columns={[
          { label: 'Schedule', render: (r) => r.schedule?.schedule_number || `SCH-${r.schedule_id}` },
          { label: 'Period', render: (r) => fmtDate(r.period_date ?? r.recognition_date) },
          { label: 'Amount', render: (r) => formatAccountingAmount(r.amount), align: 'right' },
          { label: 'Status', render: (r) => <IncomeStatusChip status={r.status} /> },
        ]}
        rowActions={(r) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(r) }];
          if (r.status === 'DRAFT' && canApprove) {
            actions.push({
              icon: PostIcon, title: 'Post', color: 'primary',
              onClick: () => confirm({
                title: 'Post recognition',
                message: 'Post this period\'s revenue recognition to the ledger?',
                onConfirm: async () => {
                  try {
                    await ledgerAPI.postRevenueRecognition(r.recognition_id);
                    showSnackbar('Recognition posted successfully', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Recognition {detail?.recognition_id ? `#${detail.recognition_id}` : ''}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>Schedule: {detail.schedule?.schedule_number || `SCH-${detail.schedule_id}`}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Period: {fmtDate(detail.period_date ?? detail.recognition_date)}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Amount: <strong>{formatAccountingAmount(detail.amount)}</strong></Typography>
              <IncomeStatusChip status={detail.status} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default RevenueRecognitionsTab;
